import React, { useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Tooltip, IconButton, Divider } from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CodeIcon from '@mui/icons-material/Code';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { EditorView, keymap, Decoration, ViewPlugin } from '@codemirror/view';
import { EditorState, StateEffect, StateField, Prec } from '@codemirror/state';
import { autocompletion, completionKeymap, startCompletion, snippet } from '@codemirror/autocomplete';
import { linter } from '@codemirror/lint';
import { history, defaultKeymap, historyKeymap } from '@codemirror/commands';
import { markdown } from '@codemirror/lang-markdown';
import { syntaxHighlighting, HighlightStyle } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import { adminAgentsAPI } from '../../../services/api';
import { toSnakeCase } from './promptUtils';

function toggleLinePrefix(view, prefix) {
  const { state } = view;
  const line = state.doc.lineAt(state.selection.main.from);
  if (line.text.startsWith(prefix)) {
    view.dispatch({ changes: { from: line.from, to: line.from + prefix.length, insert: '' } });
  } else {
    const existing = line.text.match(/^(#{1,6} |- )/);
    if (existing) {
      view.dispatch({ changes: { from: line.from, to: line.from + existing[0].length, insert: prefix } });
    } else {
      view.dispatch({ changes: { from: line.from, to: line.from, insert: prefix } });
    }
  }
  view.focus();
}

function insertCodeBlock(view) {
  const { from, to } = view.state.selection.main;
  const selected = view.state.sliceDoc(from, to);
  if (selected) {
    view.dispatch({ changes: { from, to, insert: `\`\`\`\n${selected}\n\`\`\`` } });
  } else {
    view.dispatch({ changes: { from, to, insert: '```\n\n```' }, selection: { anchor: from + 4 } });
  }
  view.focus();
}

function countUnclosedParens(text) {
  let depth = 0;
  for (const ch of text) {
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
  }
  return depth;
}

function dedentLines(lines) {
  const nonEmpty = lines.filter(l => l.trim().length > 0);
  if (nonEmpty.length === 0) return lines;
  const minIndent = nonEmpty.reduce((min, l) => Math.min(min, l.match(/^(\s*)/)[1].length), Infinity);
  const strip = Math.max(0, minIndent - 2);
  return strip === 0 ? lines : lines.map(l => l.slice(strip));
}

function formatMarkdown(view) {
  const lines = view.state.doc.toString().split('\n');
  const out = [];
  let inCode = false;
  let fenceBuffer = [];
  // Track open-paren depth so multi-line $class.method(\n...\n) bodies
  // are preserved verbatim rather than having their indentation stripped.
  let parenDepth = 0;

  const blank = () => out[out.length - 1] !== '' && out.push('');
  const isFence = (l) => /^\s*```/.test(l);

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const next = lines[i + 1];

    if (isFence(raw)) {
      const line = raw.trimStart();
      if (!inCode) {
        blank();
        out.push(line);
        inCode = true;
        fenceBuffer = [];
        parenDepth = 0;
      } else {
        // Closing fence — dedent buffered content then flush
        dedentLines(fenceBuffer).forEach(l => out.push(l));
        out.push(line);
        inCode = false;
        if (next !== undefined && next.trim() !== '') blank();
      }
      continue;
    }

    if (inCode) { fenceBuffer.push(raw); continue; }

    // Inside an unclosed tool-call argument block — preserve raw indentation.
    if (parenDepth > 0) {
      out.push(raw);
      parenDepth = Math.max(0, parenDepth + countUnclosedParens(raw));
      continue;
    }

    // Strip leading whitespace outside code blocks — indented lines break markdown rendering
    const line = raw.trimStart();
    const isHeading = /^#{1,6} /.test(line);
    const isList = /^[-*+] |^\d+\. /.test(line);
    const prevLine = out[out.length - 1];
    const prevIsList = /^[-*+] |^\d+\. /.test(prevLine || '');

    if (isHeading && prevLine !== undefined && prevLine !== '') blank();
    if (isList && prevLine !== undefined && prevLine !== '' && !prevIsList) blank();

    out.push(line);

    if (isHeading && next !== undefined && next.trim() !== '') blank();

    // If this line opens a tool call whose parens aren't closed on the same line,
    // subsequent lines are part of the argument block — preserve their indentation.
    if (/^\$\w+\.\w+\(/.test(line)) {
      parenDepth = Math.max(0, countUnclosedParens(line));
    }
  }

  const formatted = out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: formatted } });
  view.focus();
}

function TBtn({ title, onClick, children }) {
  return (
    <Tooltip title={title} placement="top" enterDelay={600}>
      <IconButton
        size="small"
        onMouseDown={(e) => { e.preventDefault(); onClick(); }}
        sx={{ borderRadius: 0.5, width: 24, height: 24, fontSize: 10, fontWeight: 'bold', color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
}

const mdHighlight = syntaxHighlighting(HighlightStyle.define([
  { tag: tags.heading1, fontWeight: 'bold', fontSize: '1.2em', color: '#1a237e' },
  { tag: tags.heading2, fontWeight: 'bold', fontSize: '1.1em', color: '#283593' },
  { tag: tags.heading3, fontWeight: 'bold', color: '#303f9f' },
  { tag: [tags.heading4, tags.heading5, tags.heading6], fontWeight: 'bold', color: '#3949ab' },
  { tag: tags.strong, fontWeight: 'bold' },
  { tag: tags.emphasis, fontStyle: 'italic' },
  { tag: tags.strikethrough, textDecoration: 'line-through', color: '#757575' },
  { tag: tags.link, color: '#1565c0', textDecoration: 'underline' },
  { tag: tags.url, color: '#1565c0' },
  { tag: tags.monospace, fontFamily: 'monospace', backgroundColor: '#f5f5f5', color: '#c62828' },
  { tag: tags.quote, color: '#546e7a', fontStyle: 'italic' },
  { tag: tags.list, color: '#37474f' },
  { tag: tags.processingInstruction, color: '#6a1b9a' },
]));

// StateEffect + StateField for valid tool method keys ("snake_class.method")
const setValidToolsEffect = StateEffect.define();
const validToolsField = StateField.define({
  create: () => new Set(),
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setValidToolsEffect)) return e.value;
    }
    return value;
  },
});

// Maps "snake_class.method" -> Set of valid parameter names
const setValidToolParamsEffect = StateEffect.define();
const validToolParamsField = StateField.define({
  create: () => new Map(),
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setValidToolParamsEffect)) return e.value;
    }
    return value;
  },
});

const toolCallPlugin = ViewPlugin.fromClass(
  class {
    constructor(view) { this.decorations = this.build(view); }
    update(update) {
      if (
        update.docChanged || update.viewportChanged ||
        update.transactions.some((tr) => tr.effects.some(
          (e) => e.is(setValidToolsEffect) || e.is(setValidToolParamsEffect)
        ))
      ) {
        this.decorations = this.build(update.view);
      }
    }
    build(view) {
      const validTools = view.state.field(validToolsField);
      const validParams = view.state.field(validToolParamsField);
      const decos = [];
      for (const { from, to } of view.visibleRanges) {
        const text = view.state.sliceDoc(from, to);
        // Mark $class.method — green if valid, red if not. For valid tools,
        // also mark the (args) region so it isn't rendered red by the code
        // fence's monospace highlight (cm-tool-arg-block overrides that).
        for (const match of text.matchAll(/\$(\w+)\.(\w+)/g)) {
          const start = from + match.index;
          const end = start + match[0].length;
          const key = `${match[1]}.${match[2]}`;
          const isKnown = validTools.size === 0 || validTools.has(key);
          decos.push(Decoration.mark({ class: isKnown ? 'cm-tool-call' : 'cm-tool-error' }).range(start, end));
          // Look immediately after the method name for `(` and walk to the
          // matching `)`, counting depth so nested calls don't terminate early.
          if (isKnown && text[match.index + match[0].length] === '(') {
            const openIdx = match.index + match[0].length;
            let depth = 0;
            let closeIdx = -1;
            for (let i = openIdx; i < text.length; i++) {
              if (text[i] === '(') depth++;
              else if (text[i] === ')') {
                depth--;
                if (depth === 0) { closeIdx = i; break; }
              }
            }
            if (closeIdx !== -1) {
              decos.push(Decoration.mark({ class: 'cm-tool-arg-block' }).range(from + openIdx, from + closeIdx + 1));
            }
          }
        }
        // Mark unknown param names inside $class.method(param=value, ...)
        for (const match of text.matchAll(/\$(\w+)\.(\w+)\(([^)]*)\)/gs)) {
          const key = `${match[1]}.${match[2]}`;
          const knownParams = validParams.get(key);
          if (!knownParams || knownParams.size === 0) continue;
          const argsStart = from + match.index + match[0].indexOf('(') + 1;
          const argsText = match[3];
          for (const paramMatch of argsText.matchAll(/(\w+)\s*=/g)) {
            const paramName = paramMatch[1];
            if (!knownParams.has(paramName)) {
              const pStart = argsStart + paramMatch.index;
              const pEnd = pStart + paramName.length;
              decos.push(Decoration.mark({ class: 'cm-tool-param-error' }).range(pStart, pEnd));
            }
          }
        }
      }
      // Sort by from position (required by Decoration.set)
      decos.sort((a, b) => a.from - b.from || a.startSide - b.startSide);
      return Decoration.set(decos);
    }
  },
  { decorations: (v) => v.decorations },
);

// StateEffect + StateField carry the set of valid sub-agent names into the decorator.
const setValidSubAgentsEffect = StateEffect.define();
const validSubAgentsField = StateField.define({
  create: () => new Set(),
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setValidSubAgentsEffect)) return e.value;
    }
    return value;
  },
});

// StateEffect + StateField carry the set of valid ticket_data field names into the decorator.
const setValidSchemaFieldsEffect = StateEffect.define();
const validSchemaFieldsField = StateField.define({
  create: () => new Set(),
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setValidSchemaFieldsEffect)) return e.value;
    }
    return value;
  },
});

const ticketDataPlugin = ViewPlugin.fromClass(
  class {
    constructor(view) { this.decorations = this.build(view); }
    update(update) {
      if (update.docChanged || update.viewportChanged || update.transactions.some((tr) => tr.effects.some((e) => e.is(setValidSchemaFieldsEffect)))) {
        this.decorations = this.build(update.view);
      }
    }
    build(view) {
      const validFields = view.state.field(validSchemaFieldsField);
      const decos = [];
      for (const { from, to } of view.visibleRanges) {
        const text = view.state.sliceDoc(from, to);
        for (const match of text.matchAll(/ticket_data\.(\w+)/g)) {
          const start = from + match.index;
          const end = start + match[0].length;
          const cls = (validFields.size > 0 && !validFields.has(match[1]))
            ? 'cm-ticket-data-error'
            : 'cm-ticket-data-field';
          decos.push(Decoration.mark({ class: cls }).range(start, end));
        }
      }
      return Decoration.set(decos);
    }
  },
  { decorations: (v) => v.decorations },
);

const subAgentPlugin = ViewPlugin.fromClass(
  class {
    constructor(view) { this.decorations = this.build(view); }
    update(update) {
      if (update.docChanged || update.viewportChanged || update.transactions.some((tr) => tr.effects.some((e) => e.is(setValidSubAgentsEffect)))) {
        this.decorations = this.build(update.view);
      }
    }
    build(view) {
      const validNames = view.state.field(validSubAgentsField);
      const decos = [];
      for (const { from, to } of view.visibleRanges) {
        const text = view.state.sliceDoc(from, to);
        for (const match of text.matchAll(/(?<!\w)@(\w+)/g)) {
          const start = from + match.index;
          const end = start + match[0].length;
          const name = match[1];
          // Only flag as error when we have a known list and this name isn't in it
          const cls = (validNames.size > 0 && !validNames.has(name))
            ? 'cm-sub-agent-error'
            : 'cm-sub-agent-call';
          decos.push(Decoration.mark({ class: cls }).range(start, end));
        }
      }
      return Decoration.set(decos);
    }
  },
  { decorations: (v) => v.decorations },
);

// Highlight {{slot_name}} tokens in the editor
const slotTokenPlugin = ViewPlugin.fromClass(
  class {
    constructor(view) { this.decorations = this.build(view); }
    update(update) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = this.build(update.view);
      }
    }
    build(view) {
      const decos = [];
      for (const { from, to } of view.visibleRanges) {
        const text = view.state.sliceDoc(from, to);
        for (const match of text.matchAll(/\{\{([a-zA-Z0-9_]+)\}\}/g)) {
          const start = from + match.index;
          const end = start + match[0].length;
          decos.push(Decoration.mark({ class: 'cm-slot-token' }).range(start, end));
        }
      }
      return Decoration.set(decos);
    }
  },
  { decorations: (v) => v.decorations },
);

// Lint unknown sub-agents and unknown version labels (advisory; does not block save)
const subAgentLinter = (subAgentsRef) => linter((view) => {
  const diagnostics = [];
  const text = view.state.doc.toString();
  const agents = subAgentsRef.current || [];
  const re = /@([\w-]+)(?::([\w-]+))?/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    const [full, name, version] = m;
    const agent = agents.find((a) => a.name === name);
    const from = m.index;
    const to = from + full.length;
    if (!agent) {
      diagnostics.push({ from, to, severity: 'warning', message: `Unknown sub-agent: '${name}'` });
      continue;
    }
    if (version && version !== 'latest' && !(agent.versions || []).includes(version)) {
      diagnostics.push({ from, to, severity: 'warning', message: `Unknown version label: '${version}'` });
    }
  }
  return diagnostics;
});

const editorTheme = EditorView.theme({
  '&': { fontSize: '11px', fontFamily: 'monospace', minHeight: '120px', backgroundColor: '#fff' },
  '.cm-content': { padding: '8px', caretColor: '#000', color: 'rgba(0,0,0,0.87)' },
  '.cm-line': { color: 'rgba(0,0,0,0.87)' },
  '.cm-scroller': { overflow: 'auto' },
  '&.cm-focused': { outline: 'none' },
  '&.cm-focused .cm-cursor': { borderLeftColor: '#000' },
  '.cm-selectionBackground, ::selection': { backgroundColor: 'rgba(25,118,210,0.2) !important' },
  '.cm-activeLine': { backgroundColor: 'rgba(0,0,0,0.03)' },
  '.cm-gutters': { display: 'none' },
  '.cm-tool-call': { color: '#22c55e', fontWeight: 'bold' },
  '.cm-tool-arg-block': { color: '#475569' },
  '.cm-tool-error': { color: '#e07a7a', fontWeight: 'bold', textDecoration: 'underline wavy #e07a7a' },
  '.cm-tool-param-error': { color: '#e07a7a', fontWeight: 'bold', textDecoration: 'underline wavy #e07a7a' },
  '.cm-sub-agent-call': { color: '#a855f7', fontWeight: 'bold' },
  '.cm-sub-agent-error': { color: '#e07a7a', fontWeight: 'bold', textDecoration: 'underline wavy #e07a7a' },
  '.cm-ticket-data-field': { color: '#f59e0b', fontWeight: 'bold' },
  '.cm-ticket-data-error': { color: '#e07a7a', fontWeight: 'bold', textDecoration: 'underline wavy #e07a7a' },
  '.cm-slot-token': { color: '#0891b2', fontWeight: 'bold', fontFamily: 'monospace' },
});

export default function PromptEditor({ agentName, content = '', onContentChange, toolMethods = [], subAgents = [], schemaFields = [], onAddToolNode, onAddSubAgentNode, readOnly = false }) {
  const editorRef = useRef(null);
  const viewRef = useRef(null);
  const toolMethodsRef = useRef(toolMethods);
  const subAgentsRef = useRef(subAgents);
  const schemaFieldsRef = useRef(schemaFields);
  const onAddToolNodeRef = useRef(onAddToolNode);
  const onAddSubAgentNodeRef = useRef(onAddSubAgentNode);

  useEffect(() => { toolMethodsRef.current = toolMethods; }, [toolMethods]);
  useEffect(() => { subAgentsRef.current = subAgents; }, [subAgents]);
  useEffect(() => { schemaFieldsRef.current = schemaFields; }, [schemaFields]);
  useEffect(() => { onAddToolNodeRef.current = onAddToolNode; }, [onAddToolNode]);
  useEffect(() => { onAddSubAgentNodeRef.current = onAddSubAgentNode; }, [onAddSubAgentNode]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view || !toolMethods.length) return;
    const validTools = new Set(toolMethods.map((m) => `${toSnakeCase(m.toolClass)}.${m.name}`));
    const validParams = new Map(
      toolMethods.map((m) => [`${toSnakeCase(m.toolClass)}.${m.name}`, new Set(m.parameters || [])])
    );
    view.dispatch({
      effects: [setValidToolsEffect.of(validTools), setValidToolParamsEffect.of(validParams)],
    });
  }, [toolMethods]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view || !subAgents.length) return;
    const validNames = new Set(subAgents.map((a) => a.name));
    view.dispatch({ effects: setValidSubAgentsEffect.of(validNames) });
  }, [subAgents]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const validFields = new Set(schemaFields.map((f) => f.name));
    view.dispatch({ effects: setValidSchemaFieldsEffect.of(validFields) });
  }, [schemaFields]);

  const toolParamCompletionSource = useCallback((context) => {
    // Only active when cursor is inside an unclosed $class.method( call
    const textBefore = context.state.sliceDoc(0, context.pos);
    const callMatch = textBefore.match(/\$(\w+)\.(\w+)\([^)]*$/s);
    if (!callMatch) return null;
    const key = `${callMatch[1]}.${callMatch[2]}`;
    const method = toolMethodsRef.current.find(
      (m) => `${toSnakeCase(m.toolClass)}.${m.name}` === key
    );
    if (!method?.parameters?.length) return null;
    const wordMatch = context.matchBefore(/\w*/);
    if (!wordMatch) return null;
    const query = wordMatch.text.toLowerCase();
    const options = method.parameters
      .filter((p) => p.toLowerCase().includes(query))
      .map((p) => ({
        label: `${p}=`,
        detail: 'parameter',
        apply: (view, completion, from, to) => {
          view.dispatch({ changes: { from, to, insert: `${p}=` } });
        },
      }));
    return options.length ? { from: wordMatch.from, options } : null;
  }, []);

  const ticketDataCompletionSource = useCallback((context) => {
    const match = context.matchBefore(/ticket_data\.\w*/);
    if (!match) return null;
    const query = match.text.slice('ticket_data.'.length).toLowerCase();
    const options = schemaFieldsRef.current
      .filter((f) => f.name.toLowerCase().includes(query))
      .slice(0, 10)
      .map((f) => ({ label: `ticket_data.${f.name}`, detail: f.type || 'string' }));
    return options.length ? { from: match.from, options, validFor: /^ticket_data\.\w*/ } : null;
  }, []);

  const dollarCompletionSource = useCallback((context) => {
    const match = context.matchBefore(/\$[\w.]*/);
    if (!match) return null;
    if (match.from > 0 && /\S/.test(context.state.sliceDoc(match.from - 1, match.from))) return null;

    const text = match.text.slice(1); // strip $
    const dotIndex = text.indexOf('.');

    if (dotIndex === -1) {
      // Phase 1: show tool classes
      const query = text.toLowerCase();
      const classes = [...new Set(toolMethodsRef.current.map((m) => toSnakeCase(m.toolClass)))];
      const options = classes
        .filter((cls) => cls.includes(query))
        .slice(0, 8)
        .map((cls) => ({
          label: `$${cls}`,
          detail: 'tool class',
          apply: (view, _completion, from, to) => {
            view.dispatch({ changes: { from, to, insert: `$${cls}.` } });
            setTimeout(() => startCompletion(view), 0);
          },
        }));
      return options.length ? { from: match.from, options, validFor: /^\$\w*/ } : null;
    }

    // Phase 2: show methods of the selected class
    const classSnake = text.slice(0, dotIndex);
    const methodQuery = text.slice(dotIndex + 1).toLowerCase();
    const methods = toolMethodsRef.current.filter((m) => toSnakeCase(m.toolClass) === classSnake);
    const options = methods
      .filter((m) => m.name.toLowerCase().includes(methodQuery))
      .slice(0, 8)
      .map((m) => {
        const params = m.parameters || [];
        // Build snippet: $class.method(#{1:param1}, #{2:param2}) or no parens if no params
        const snippetTemplate = params.length
          ? `$${classSnake}.${m.name}(\n${params.map((p, i) => `  ${p}=\${${i + 1}:${p}}`).join(',\n')}\n)`
          : `$${classSnake}.${m.name}()`;
        return {
          label: `$${classSnake}.${m.name}`,
          detail: m.description || m.toolClass,
          info: params.length ? `Parameters: ${params.join(', ')}` : 'No parameters',
          apply: (view, completion, from, to) => {
            snippet(snippetTemplate)(view, completion, from, to);
            if (onAddToolNodeRef.current) onAddToolNodeRef.current(m);
          },
        };
      });
    return options.length ? { from: match.from, options, validFor: /^\$[\w.]*/ } : null;
  }, []);

  const atCompletionSource = useCallback((context) => {
    const match = context.matchBefore(/@[\w-]*(:[\w-]*)?/);
    if (!match) return null;
    const text = match.text;
    const colonIdx = text.indexOf(':');
    const agents = subAgentsRef.current;

    // phase 2: after `:` complete version labels for the matched sub-agent
    if (colonIdx !== -1) {
      const namePart = text.slice(1, colonIdx);
      const labelPart = text.slice(colonIdx + 1).toLowerCase();
      const agent = agents.find((a) => a.name === namePart);
      if (!agent) return null;
      const versions = ['latest', ...(agent.versions || [])];
      const versionOptions = versions
        .filter((v) => v.toLowerCase().startsWith(labelPart) || labelPart === '')
        .map((v) => ({ label: v, type: 'version', detail: 'version' }));
      return versionOptions.length
        ? { from: match.from + colonIdx + 1, options: versionOptions }
        : null;
    }

    // phase 1: complete sub-agent names (with create-new fallback)
    const query = text.slice(1).toLowerCase();
    const matches = agents.filter((a) => a.name.toLowerCase().includes(query));
    const exactMatch = matches.some((a) => a.name.toLowerCase() === query);
    const isNew = query.trim() && !exactMatch;
    const options = [
      ...matches.slice(0, 8).map((a) => ({
        label: `@${a.name}`,
        detail: 'sub-agent',
        apply: (view, completion, from, to) => {
          view.dispatch({ changes: { from, to, insert: completion.label } });
          if (onAddSubAgentNodeRef.current) onAddSubAgentNodeRef.current({ agentName: a.name });
        },
      })),
      ...(isNew ? [{
        label: `@${query}`,
        detail: 'create new sub-agent',
        apply: (view, completion, from, to) => {
          view.dispatch({ changes: { from, to, insert: completion.label } });
          adminAgentsAPI.createSubAgent(agentName, { name: query, description: '' }).catch(() => {});
          if (onAddSubAgentNodeRef.current) onAddSubAgentNodeRef.current({ agentName: query });
        },
      }] : []),
    ];
    return options.length ? { from: match.from, options } : null;
  }, [agentName]);

  useEffect(() => {
    if (!editorRef.current) return;
    const extensions = [
      markdown(),
      mdHighlight,
      editorTheme,
      EditorView.lineWrapping,
      validToolsField,
      validToolParamsField,
      validSubAgentsField,
      Prec.highest(toolCallPlugin),
      Prec.highest(subAgentPlugin),
      Prec.highest(ticketDataPlugin),
      Prec.highest(slotTokenPlugin),
      validSchemaFieldsField,
      subAgentLinter(subAgentsRef),
    ];
    if (readOnly) {
      extensions.push(EditorState.readOnly.of(true));
      extensions.push(EditorView.theme({ '.cm-cursor': { display: 'none' } }));
    } else {
      extensions.push(history());
      extensions.push(keymap.of([...defaultKeymap, ...historyKeymap, ...completionKeymap]));
      extensions.push(autocompletion({ override: [toolParamCompletionSource, ticketDataCompletionSource, dollarCompletionSource, atCompletionSource] }));
      extensions.push(EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          let triggerComplete = false;
          update.changes.iterChanges((_fA, _tA, _fB, _tB, inserted) => {
            const t = inserted.toString();
            if (t === '$' || t === '@' || t === '.' || t === '(' || t === ':') triggerComplete = true;
          });
          if (triggerComplete) startCompletion(update.view);
          if (onContentChange) onContentChange(update.state.doc.toString());
        }
      }));
    }
    const view = new EditorView({
      state: EditorState.create({ doc: content, extensions }),
      parent: editorRef.current,
    });
    viewRef.current = view;
    return () => { view.destroy(); viewRef.current = null; };
  }, [dollarCompletionSource, atCompletionSource, readOnly]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view || !readOnly) return;
    const current = view.state.doc.toString();
    if (current !== content) {
      view.dispatch({ changes: { from: 0, to: current.length, insert: content } });
    }
  }, [content, readOnly]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {!readOnly && <Typography variant="overline" sx={{ fontSize: 9 }}>Prompt Content</Typography>}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        {!readOnly && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 0.5, py: 0.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <TBtn title="Heading 1" onClick={() => toggleLinePrefix(viewRef.current, '# ')}>H1</TBtn>
            <TBtn title="Heading 2" onClick={() => toggleLinePrefix(viewRef.current, '## ')}>H2</TBtn>
            <TBtn title="Heading 3" onClick={() => toggleLinePrefix(viewRef.current, '### ')}>H3</TBtn>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
            <TBtn title="Bullet list" onClick={() => toggleLinePrefix(viewRef.current, '- ')}>
              <FormatListBulletedIcon sx={{ fontSize: 14 }} />
            </TBtn>
            <TBtn title="Code block" onClick={() => insertCodeBlock(viewRef.current)}>
              <CodeIcon sx={{ fontSize: 14 }} />
            </TBtn>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
            <TBtn title="Format markdown" onClick={() => formatMarkdown(viewRef.current)}>
              <AutoFixHighIcon sx={{ fontSize: 14 }} />
            </TBtn>
          </Box>
        )}
        <Box ref={editorRef} sx={{ cursor: 'text', minHeight: 120 }} onClick={() => viewRef.current?.focus()} />
      </Box>
    </Box>
  );
}
